import type { V3CanvasHomeFeaturedListItem, V3CanvasHomeFeaturedListParams } from './type';

const STORAGE_KEY = 'roboneo-featured-queue-v1';
const MAX_AGE = 30 * 60 * 1000;
interface QueueSnapshot {
  context: string;
  savedAt: number;
  items: V3CanvasHomeFeaturedListItem[];
}
let memorySnapshot: QueueSnapshot | undefined;

/** 按实际投放与筛选隔离队列，不因当前播放 ID 或接口 limit 改变而丢失列表。 */
function contextKey(params: V3CanvasHomeFeaturedListParams) {
  return JSON.stringify([params.placement ?? 'website_home', params.region ?? '', params.locale ?? '', String(params.category_id ?? '')]);
}

/** 只接受可展示的有效案例，去重并保留接口顺序。 */
export function normalizeFeaturedQueue(items: V3CanvasHomeFeaturedListItem[]): V3CanvasHomeFeaturedListItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const id = String(item?.featured_id);
    if (!/^[1-9]\d*$/.test(id) || !item?.case || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** 点击列表某项时只轮转一次，后续播放按同一顺序循环。 */
export function rotateFeaturedQueue(items: V3CanvasHomeFeaturedListItem[], featuredId: string) {
  const queue = normalizeFeaturedQueue(items);
  const index = queue.findIndex((item) => String(item.featured_id) === featuredId);
  return index < 0 ? queue : [...queue.slice(index), ...queue.slice(0, index)];
}

/** 同页签保存公开卡片队列以支持跳页、刷新和登录恢复；缓存不用于权限判断。 */
export function saveFeaturedQueue(params: V3CanvasHomeFeaturedListParams, items: V3CanvasHomeFeaturedListItem[], featuredId: string) {
  const queue = rotateFeaturedQueue(items, featuredId).map((item) => ({
    ...item,
    // 仅保留列表展示数据，不能将详情返回的工作流快照写入浏览器缓存。
    case: { ...item.case, graph_json: undefined },
  }));
  memorySnapshot = { context: contextKey(params), savedAt: Date.now(), items: queue };
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memorySnapshot)); } catch { /* 禁用存储时保留当前页面内存队列。 */ }
  return queue;
}

/** 仅恢复当前上下文且包含所选案例的队列；过期或损坏缓存交由页面独立列表请求恢复。 */
export function readFeaturedQueue(params: V3CanvasHomeFeaturedListParams, featuredId: string): V3CanvasHomeFeaturedListItem[] | undefined {
  let snapshot = memorySnapshot;
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    try { snapshot = stored ? JSON.parse(stored) : undefined; } catch { return undefined; }
  } catch { /* 存储不可用时尝试内存快照。 */ }
  if (!snapshot || snapshot.context !== contextKey(params) || !Number.isFinite(snapshot.savedAt)
    || Date.now() - snapshot.savedAt > MAX_AGE || !Array.isArray(snapshot.items)) return undefined;
  const queue = normalizeFeaturedQueue(snapshot.items);
  return queue.some((item) => String(item.featured_id) === featuredId) ? queue : undefined;
}
